import { Injectable } from '@angular/core';

import { StageService } from '../stage.service';
import { SetWallMaterialService } from '../set-wall-material.service';
import { ConstantsService } from '../constants.service';
import { RailingGenerationService } from '../railing/services/railing-generation.service';
import { ProductsService } from '../products.service';
import { LoadingSpinnerService } from '../loading-spinner.service';
import { Model3DService } from '../model3-d.service';
import { WallCutService } from './wall-cut.service';
import { ScaleInfo } from '../../Component(2D and 3D)/Shared/ObjectResize/ScaleInfo';
import { ObjectResizer } from '../../Component(2D and 3D)/Shared/ObjectResize/ObjectResizer';
import { HelperLinesService } from '../../services/helper-lines.service';
import { AwningColorTextureService } from '../awning/awning-color-texture.service';

declare const THREE: any;

@Injectable()
export class WallBuilderService {
  private scaleInfo: ScaleInfo = new ScaleInfo();
  private objectResizer = new ObjectResizer();

  constructor(
    private stage: StageService,
    private wallMaterialSrvc: SetWallMaterialService,
    private constantsService: ConstantsService,
    private railingGeneration: RailingGenerationService,
    private productSrvc: ProductsService,
    private loadingSpinnerService: LoadingSpinnerService,
    private model3D: Model3DService,
    private wallCutService: WallCutService,
    private helperLinesService: HelperLinesService,
    private awningColorTextureService: AwningColorTextureService
  ) {}

  private isTiersSuperstructureType(point) {
    if (point) {
      return (
        point.superstructureType === 'tier1StepDown' ||
        point.superstructureType === 'tier2StepDown' ||
        point.superstructureType === 'tier3StepDown' ||
        point.superstructureType === 'tier1StepUp' ||
        point.superstructureType === 'tier2StepUp' ||
        point.superstructureType === 'tier3StepUp'
      );
    }
  }

  private restrictWallOnTiers(config, onFirstPoint: boolean) {
    const firstPoint = new THREE.Vector3(config.firstPoint.x, 0, config.firstPoint.y);
    const secondPoint = new THREE.Vector3(config.secondPoint.x, 0, config.secondPoint.y);
    const p1ToP2 = new THREE.Vector3(
      config.secondPoint.x - config.firstPoint.x,
      0,
      config.secondPoint.y - config.firstPoint.y
    );
    const p2ToP1 = new THREE.Vector3(
      config.firstPoint.x - config.secondPoint.x,
      0,
      config.firstPoint.y - config.secondPoint.y
    );

    let shiftLength;
    const superstructureType = onFirstPoint
      ? config.previousPoint.superstructureType
      : config.secondPoint.superstructureType;

    switch (superstructureType) {
      case 'tier3StepUp':
      case 'tier3StepDown':
        shiftLength = 23.5;
        break;

      case 'tier2StepUp':
      case 'tier2StepDown':
        shiftLength = 12.5;
        break;

      case 'tier1StepUp':
      case 'tier1StepDown':
        shiftLength = 0;
        break;
    }

    let projectBaseEdgeWallEdge;

    if (onFirstPoint) {
      const shiftToTiers = p2ToP1.clone();
      shiftToTiers.setLength(Math.abs(config.distance - config.width / 2));

      if (config.distance > config.width / 2) {
        shiftToTiers.negate();
      }

      const wallEdge = firstPoint.clone().add(shiftToTiers);

      const p1Edge = firstPoint.clone().add(p2ToP1.clone().setLength(shiftLength));

      const baseEdge = new THREE.Vector3(
        p1Edge.x - config.secondPoint.x,
        0,
        p1Edge.z - config.secondPoint.y
      );
      const p1ToWallEdge = new THREE.Vector3(wallEdge.x - p1Edge.x, 0, wallEdge.z - p1Edge.z);

      projectBaseEdgeWallEdge =
        (baseEdge.x * p1ToWallEdge.x + baseEdge.z * p1ToWallEdge.z) / baseEdge.length();

      if (projectBaseEdgeWallEdge > 0) {
        config.width = config.width - projectBaseEdgeWallEdge;
        config.distance = config.distance + projectBaseEdgeWallEdge / 2;
        config.restrictedOnMovingToTiers = true;
      }
    } else {
      const wallEdge = firstPoint
        .clone()
        .add(p1ToP2.clone().setLength(config.distance + config.width / 2));

      const p2Edge = secondPoint.clone().add(p1ToP2.clone().setLength(shiftLength));

      const baseEdge = new THREE.Vector3(
        p2Edge.x - config.firstPoint.x,
        0,
        p2Edge.z - config.firstPoint.y
      );
      const p2ToWallEdge = new THREE.Vector3(wallEdge.x - p2Edge.x, 0, wallEdge.z - p2Edge.z);

      projectBaseEdgeWallEdge =
        (baseEdge.x * p2ToWallEdge.x + baseEdge.z * p2ToWallEdge.z) / baseEdge.length();

      if (projectBaseEdgeWallEdge > 0) {
        config.width = config.width - projectBaseEdgeWallEdge;
        config.distance = config.distance - projectBaseEdgeWallEdge / 2;
        config.restrictedOnMovingToTiers = true;
      }
    }
  }

  createWall(config: any): any {
    let oldWall = this.stage
        .getWalls()
        .find((obj: any) => obj.userData.uuid === config.firstPoint.superstructureId),
      floor: any,
      children = [];

    if (oldWall) {
      for (let i = 0; i < oldWall.children.length; i++) {
        const child = oldWall.children[i];
        children.push(child);
      }
      for (let i = 0; i < oldWall.children.length; i++) {
        const child = oldWall.children[i];
        oldWall.remove(child);
      }
      this.stage.remove(oldWall);
    }

    if (children.length && config.shiftY) {
      children.forEach(child => {
        child.position.y += config.shiftY / 2;
      });
      delete config.shiftY;
    } else if (config.shiftY) {
      delete config.shiftY;
    }

    floor = this.stage.getFloorById(config.floorId);

    if (this.isTiersSuperstructureType(config.previousPoint)) {
      this.restrictWallOnTiers(config, true);
    }

    if (this.isTiersSuperstructureType(config.secondPoint)) {
      this.restrictWallOnTiers(config, false);
    }

    let wallHeight = config.height,
      wallLength = config.width,
      wallDepth = this.constantsService.wallDepth;

    let vector = new THREE.Vector3(
      config.secondPoint.x - config.firstPoint.x,
      0,
      config.secondPoint.y - config.firstPoint.y
    );
    vector.setLength(config.distance);
    let perpendicular = new THREE.Vector3(vector.z, 0, -vector.x);
    if (config.distance < 0) {
      perpendicular.negate();
    }
    let norm = perpendicular.normalize();
    norm.setLength(wallDepth / 2);

    let position = new THREE.Vector3(config.firstPoint.x, wallHeight / 2, config.firstPoint.y);
    position.add(vector);

    let geometry = new THREE.BoxGeometry(wallLength, wallHeight, wallDepth);

    let tempSyncMaterial = new THREE.MeshLambertMaterial({ color: new THREE.Color(0xd3d3d3) });
    let wall = new THREE.Mesh(geometry, tempSyncMaterial);
    let angle = -Math.atan2(vector.z, vector.x);

    if (config.distance < 0) {
      angle -= Math.PI;
    }

    wall.name = 'wall';
    wall.position.set(position.x, position.y, position.z);
    wall.position.add(norm);
    wall.rotation.y = angle;
    wall.userData.isDeckComponent = true;
    wall.userData.config = config;
    wall.castShadow = true;
    wall.receiveShadow = true;

    config.firstPoint.distance = config.distance;
    config.firstPoint.width = config.width;
    config.firstPoint.depth = wallDepth;
    config.firstPoint.superstructureType = 'wall';
    config.firstPoint.height = config.height;
    config.firstPoint.superstructureId = config.firstPoint.superstructureId || wall.id;
    config.firstPoint.material = config.material;

    wall.userData.uuid = config.firstPoint.superstructureId;

    this.stage.add(wall);
    THREE.SceneUtils.attach(wall, this.stage.getScene(), floor);
    this.railingGeneration.buildRailing(this.railingGeneration.IsVisible());

    this.wallMaterialSrvc.setSelectedWall(wall);
    this.wallMaterialSrvc.changeSelectedWallMaterial(config.material, wall);
    if (children.length) {
      children.forEach(child => wall.add(child));
    }
    return wall;
  }

  async createWindowsDoors(objConfigs, wall) {
    for (const objConfig of objConfigs) {
      const entity = await this.productSrvc.getDoorsAndWindows();

      const activeEntity = JSON.parse(
        JSON.stringify(entity.find(product => product.id === objConfig.id))
      );

      const rotation = new THREE.Euler(
        objConfig.rotation.x,
        objConfig.rotation.y,
        objConfig.rotation.z,
        'XYZ'
      );
      this.loadingSpinnerService.spinnerLoadingListAdd('createWallObjects' + objConfig.id);

      const obj = await this.model3D.create(activeEntity, {
        position: objConfig.position,
        rotation: rotation,
        wall: null,
        show3DObject: true
      });

      obj.userData.entity = activeEntity;
      obj.name = activeEntity.productType;
      wall.updateMatrixWorld(true);
      obj.updateMatrixWorld(true);
      this.stage.getScene().updateMatrixWorld(true);
      THREE.SceneUtils.attach(obj, this.stage.getScene(), wall);
      this.objectResizer.resize({
        object: obj,
        scaleInfo: this.scaleInfo,
        newSize: { length: objConfig.length, height: objConfig.height },
        currentSize: { length: activeEntity.length, height: activeEntity.height },
        includeChildren: true
      });
      obj.userData.entity.height = objConfig.height;
      obj.userData.entity.length = objConfig.length;
      this.wallMaterialSrvc.changeSelectedWallMaterial();

      wall.material.length && this.wallCutService.cutHoles(wall);

      this.loadingSpinnerService.spinnerLoadingListRemove('createWallObjects' + objConfig.id);
    }
  }

  async createTrellis(configs, wall) {
    for (const config of configs) {
      const entities = await this.productSrvc.getTrellis();
      const entity = entities.find(product => product.id === config.id);
      if (!entity) {
        continue;
      }
      const activeEntity = JSON.parse(JSON.stringify(entity));

      const rotation = new THREE.Euler(
        config.rotation.x,
        config.rotation.y,
        config.rotation.z,
        'XYZ'
      );
      this.loadingSpinnerService.spinnerLoadingListAdd('createWallObjects' + config.id);

      const obj = await this.model3D.create(activeEntity, {
        position: config.position,
        rotation: rotation,
        wall: null,
        show3DObject: true
      });

      obj.userData.entity = activeEntity;
      obj.name = activeEntity.productType;
      wall.updateMatrixWorld(true);
      obj.updateMatrixWorld(true);
      this.stage.getScene().updateMatrixWorld(true);
      THREE.SceneUtils.attach(obj, this.stage.getScene(), wall);

      this.loadingSpinnerService.spinnerLoadingListRemove('createWallObjects' + config.id);
    }
  }

  async createAwnings(configs, parent) {
    // set color, texture to use for awning
    if (
      configs.texture &&
      configs.texture.name !== this.awningColorTextureService.glassTexture.name
    ) {
      this.awningColorTextureService.glassTexture = configs.texture;
    }

    if (
      configs.color &&
      configs.color.name !== this.awningColorTextureService.frameColor.name
    ) {
      this.awningColorTextureService.frameColor = configs.color;
    }

    for (const config of configs.items) {
      const entities = await this.productSrvc.getAwnings();

      const activeEntity = JSON.parse(
        JSON.stringify(entities.find(product => product.id === config.id))
      );

      const rotation = new THREE.Euler(
        config.rotation.x,
        config.rotation.y,
        config.rotation.z,
        'XYZ'
      );
      this.loadingSpinnerService.spinnerLoadingListAdd('createWallObjects' + config.id);

      const obj = await this.model3D.create(activeEntity, {
        position: config.position,
        rotation: rotation,
        wall: null,
        show3DObject: true
      });

      obj.userData.entity = activeEntity;
      obj.name = activeEntity.productType;
      parent.updateMatrixWorld(true);
      obj.updateMatrixWorld(true);
      this.stage.getScene().updateMatrixWorld(true);
      THREE.SceneUtils.attach(obj, this.stage.getScene(), parent);
      this.awningColorTextureService.setGlassTextureForOneAwning(obj);
      this.awningColorTextureService.setFrameColorForOneAwning(obj);

      this.loadingSpinnerService.spinnerLoadingListRemove('createWallObjects' + config.id);

      if (config.extensions && config.extensions.length) {
        await this.createAwnings({ items: config.extensions }, obj);
      }
    }
  }

  rescaleWalls(value) {
    const walls = this.stage.getWalls();
    for (let i = 0; i < walls.length; i++) {
      walls[i].userData.config.shiftY = value;
      if (walls[i].userData.config.height + value > 106.68) {
        if (value <= 30.48 && value >= -30.48) {
          walls[i].userData.config.height += value;
          this.createWall(walls[i].userData.config);
        } else {
          walls[i].userData.config.height =
            walls[i].parent.getWorldPosition(new THREE.Vector3()).y +
            this.constantsService.wallHeight;
          this.createWall(walls[i].userData.config);
        }
      } else {
        this.createWall(walls[i].userData.config);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/services/wall/wall-builder.service.ts